import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
/**
 * Represents the data structure for newsletter form submission.
 * @typedef {Object} NewsletterFormData
 * @property {boolean} marketing_authorization - Indicates user's marketing communication consent
 * @property {string[]} [brands_preferences] - Optional array of brand preferences
 * @property {string[]} [channels_preferences] - Optional array of communication channel preferences
 * @property {string} [prefix] - Optional phone number prefix
 * @property {string} [phoneNumber] - Optional phone number
 */
export type NewsletterFormData = {
  marketing_authorization: boolean
  brands_preferences?: string[]
  channels_preferences?: string[]
  prefix?: string
  phoneNumber?: string
}

/**
 * Enum representing different steps in the newsletter subscription process.
 * @enum {string}
 */
export enum NEWSLETTER_STEP {
  SUBSCRIBE = 'subscribe',
  PREFERENCE = 'confirmationPreference',
  THANK_YOU = 'thankYou',
  LOGIN = 'login',
}

/**
 * Composable for managing newsletter subscription functionality.
 * Handles user email, newsletter steps, and Salesforce user interactions.
 *
 * @returns {Object} Newsletter management utilities
 * @property {Ref<string>} email - Current newsletter email
 * @property {Ref<string>} salesforceId - Salesforce user ID
 * @property {Ref<NEWSLETTER_STEP>} step - Current newsletter subscription step
 * @property {Function} createNewsletterUser - Creates a new newsletter user in Salesforce
 * @property {Function} updateNewsletterUser - Updates an existing newsletter user
 * @property {Function} openNewsletterSidebar - Opens newsletter sidebar and handles user flow
 * @property {Function} setCurrentStep - Sets the current newsletter subscription step
 * @property {Function} setNewsletterEmail - Sets the newsletter email
 * @property {Function} clearNewsletterLocalStorage - Clears newsletter-related local storage
 * @property {ComputedRef<string[]>} brandPreference - Computed brand preferences
 *
 * @example
 * const {
 *   email,
 *   openNewsletterSidebar,
 *   setCurrentStep
 * } = useNewsletter()
 *
 * openNewsletterSidebar('user@example.com')
 */
export async function useNewsletter() {
  const { openDialog } = useDialog(SIDEBAR_IDS.newsletterSidebar)
  const { emailExists, getSFUserFromEmail } = useAuth0()
  const nuxtApp = useNuxtApp()

  const { isLogged } = useLogin()
  const isLoading = useLoaderState()

  const email = useState<string>('newsletter-email', () => '')
  const salesforceId = useState<string>('newsletter-salesforceId', () => '')
  const step = useState<NEWSLETTER_STEP>(
    'newsletter-step',
    () => NEWSLETTER_STEP.SUBSCRIBE
  )
  const { currentWebsite, isBrand } = useWebsite()
  const config = useAppConfig()
  const country = config.currentCountry as string
  const language = config.currentLanguage
  const { customer } = await nuxtApp.runWithContext(
    async () => await useCustomer()
  )

  const { getBrandAcronym } = await nuxtApp.runWithContext(
    async () => await useBrandsSettings()
  )

  const brandPreference = computed(() => {
    if (isBrand(currentWebsite.value)) {
      return [getBrandAcronym(currentWebsite.value)]
    } else {
      return Array.from(new Set(brands.map(item => getBrandAcronym(item))))
    }
  })

  /**
   * Clears newsletter-related local storage.
   * Removes the 'nl-login-tmp' item from localStorage.
   */
  const clearNewsletterLocalStorage = () => {
    localStorage.removeItem('nl-login-tmp')
  }

  /**
   * Opens the newsletter sidebar and handles user subscription flow.
   *
   * @param {string} email - User's email address
   * @throws {Error} If there are issues with user creation or retrieval
   */
  const openNewsletterSidebar = async (email: string) => {
    try {
      clearNewsletterLocalStorage()
      setNewsletterEmail(email)
      if (!isLogged.value) {
        const exists = await emailExists(email)
        if (exists && exists.sfExists && exists.userOrFail) {
          setCurrentStep(NEWSLETTER_STEP.LOGIN)
        } else if (exists && exists.sfExists) {
          //retrive sfId and send update request
          const user = await getSFUserFromEmail(email)
          //this is done for type checking, but since exists.sfExists is true, user should never be false
          if (!user) throw createError({ message: 'User not found' })
          salesforceId.value = user.salesforceId!
          await updateNewsletterUser(email, user.salesforceId!, {
            marketing_authorization: true,
            brands_preferences: brandPreference.value.filter(
              (item): item is string => item !== undefined
            ),
          })
          setCurrentStep(NEWSLETTER_STEP.PREFERENCE)
        } else {
          const createNewsletterUserResponse = await createNewsletterUser(
            email,
            brandPreference.value.filter(
              (item): item is string => item !== undefined
            )
          )
          if (createNewsletterUserResponse) {
            salesforceId.value = createNewsletterUserResponse.sfId
            setCurrentStep(NEWSLETTER_STEP.PREFERENCE)
          }
        }
      } else {
        if (!email) setNewsletterEmail(customer?.value?.email || '')
        setCurrentStep(NEWSLETTER_STEP.PREFERENCE)
      }
      openDialog(SIDEBAR_IDS.newsletterSidebar)
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Creates a new newsletter user in Salesforce.
   *
   * @param {string} email - User's email address
   * @param {string[]} brandPreference - Array of brand preferences
   * @returns {Promise<{sfId: string}>} Created user's Salesforce ID
   * @throws {Error} If user creation fails
   */
  const createNewsletterUser = async (
    email: string,
    brandPreference: string[]
  ) => {
    isLoading.value = true
    try {
      const { salesforceId: sfId } = await $fetch<{ salesforceId: string }>(
        '/api/newsletter/createSfUser',
        {
          method: 'POST',
          body: {
            email: email,
            brandPreference,
            currentCountry: country,
            currentLanguage: language,
          },
        }
      )
      return { sfId }
    } catch (err) {
      // @ts-expect-error
      const message = err?.errors ?? err?.message
      throw createError({ message })
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Updates an existing newsletter user in Salesforce.
   *
   * @param {string} email - User's email address
   * @param {string} salesforceId - User's Salesforce ID
   * @param {NewsletterFormData} data - Updated newsletter user data
   * @returns {Promise<any>} Update response from Salesforce
   * @throws {Error} If user update fails
   */
  const updateNewsletterUser = async (
    email: string,
    salesforceId: string,
    data: NewsletterFormData
  ) => {
    isLoading.value = true
    try {
      return await $fetch('/api/newsletter/updateSfUser', {
        method: 'POST',
        body: {
          email: email,
          sfId: salesforceId,
          data: data,
          language: language,
          country: country,
        },
      })
    } catch (err) {
      // @ts-expect-error
      const message = err?.errors ?? err?.message
      throw createError({ message })
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sets the current newsletter subscription step.
   *
   * @param {NEWSLETTER_STEP} newStep - New newsletter subscription step
   */
  const setCurrentStep = (newStep: NEWSLETTER_STEP) => {
    step.value = newStep
  }

  /**
   * Sets the newsletter email address.
   *
   * @param {string} newEmail - New email address
   */
  const setNewsletterEmail = (newEmail: string) => {
    email.value = newEmail
  }

  return {
    email,
    salesforceId,
    step,
    createNewsletterUser,
    updateNewsletterUser,
    openNewsletterSidebar,
    setCurrentStep,
    setNewsletterEmail,
    clearNewsletterLocalStorage,
    brandPreference,
  }
}
